import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import classes from './SavedSearches.module.scss';
import { updateUserData } from '../../services';
import AuthContext from '../../store/auth-context';

// The saved-searches panel for both the auction page and Dealer Stock. What a search
// is differs between them, so each page passes an adapter (auctionSavedSearches.js,
// helper/dealerSavedSearches.js):
//   field          the user-record list the searches live in
//   keyOf(entry)   identity: two entries with the same key are the same search
//   describe(entry) what it picks, as short parts ("MAZDA", "RX-7", "1992–2002")
//   collapsedKey, carsLabel, listingsLabel  storage key and wording
// `current` is the search on screen in the same shape as a saved entry; saving it
// stores { name, ...current, notifyEnabled, createdAt }.
const VISIBLE_SEARCHES = 4;
const NAME_MAX_LENGTH = 40;

// Whether the viewer has minimized the saved-search list; a per-browser convenience.
const readCollapsed = (key) => {
  try {
    return localStorage.getItem(key) === '1';
  } catch (e) {
    return false;
  }
};

const suggestName = (describe, current, savedSearches) => {
  const base = describe(current).slice(0, 3).join(' · ').slice(0, NAME_MAX_LENGTH).trim() || 'My search';
  const taken = savedSearches.map((saved) => saved.name.trim().toLowerCase());
  let name = base;
  for (let n = 2; taken.includes(name.toLowerCase()); n++) {
    name = `${base} (${n})`;
  }
  return name;
};

const AlertSwitch = ({ checked, onToggle, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    title={checked ? 'Alerts on: we email you new matches. Click to turn off.' : 'Alerts off. Click to get emails for new matches.'}
    className={`${classes.alertSwitch} ${checked ? classes.alertSwitchOn : ''}`}
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
  >
    <i className={checked ? 'fas fa-bell' : 'far fa-bell-slash'} aria-hidden="true" />
    <span className={classes.track}>
      <span className={classes.thumb} />
    </span>
  </button>
);

const SavedSearches = ({ savedSearches, setSavedSearches, adapter, current, onApply }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { isLoggedIn, patchUserData } = useContext(AuthContext);
  const [isNaming, setIsNaming] = useState(false);
  const [name, setName] = useState('');
  const [alertsOn, setAlertsOn] = useState(true);
  const [error, setError] = useState('');
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [collapsed, setCollapsed] = useState(() => readCollapsed(adapter.collapsedKey));
  const [infoOpen, setInfoOpen] = useState(false);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    setConfirmDeleteIndex(null);
    try {
      localStorage.setItem(adapter.collapsedKey, next ? '1' : '0');
    } catch (e) {
      // Private mode or blocked storage: it just won't be remembered next visit.
    }
  };

  const alertsOnCount = savedSearches.filter((saved) => saved.notifyEnabled).length;

  const hasCriteria = adapter.describe(current).length > 0;
  const currentKey = adapter.keyOf(current);
  const currentIndex = savedSearches.findIndex((saved) => adapter.keyOf(saved) === currentKey);
  const currentSaved = currentIndex > -1 ? savedSearches[currentIndex] : null;

  // Show the list from the top, but never hide the search that is on screen right now:
  // its row is where that search's switch lives, so it stays even with the list minimized.
  const visibleSearches = savedSearches
    .map((saved, index) => ({ saved, index }))
    .filter(({ index }) => index === currentIndex || (!collapsed && (showAll || index < VISIBLE_SEARCHES)));

  const persist = (updated, successMessage) => {
    const previous = savedSearches;
    setSavedSearches(updated);
    return updateUserData({ [adapter.field]: updated })
      .then((res) => {
        if (res?.message !== 'User data updated successfully!') {
          throw new Error(res?.message || 'Update failed');
        }
        patchUserData({ [adapter.field]: updated });
        enqueueSnackbar(successMessage, { variant: 'success', autoHideDuration: 3500 });
      })
      .catch(() => {
        setSavedSearches(previous);
        enqueueSnackbar("Couldn't save that change. Please try again.", { variant: 'error', autoHideDuration: 4000 });
      });
  };

  const startNaming = () => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    setName(suggestName(adapter.describe, current, savedSearches));
    setAlertsOn(true);
    setError('');
    setIsNaming(true);
  };

  const saveCurrentSearch = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Give this search a name.');
      return;
    }
    if (savedSearches.some((saved) => saved.name.trim().toLowerCase() === trimmed.toLowerCase())) {
      setError(`You already have a search called "${trimmed}".`);
      return;
    }
    // createdAt lets the alert job skip cars that were listed before the search was saved.
    const entry = { name: trimmed, ...current, notifyEnabled: alertsOn, createdAt: new Date().toISOString() };
    setIsNaming(false);
    persist(
      [...savedSearches, entry],
      alertsOn ? `Saved "${trimmed}". We'll email you when new cars match.` : `Saved "${trimmed}".`
    );
  };

  const toggleAlerts = (index) => {
    const target = savedSearches[index];
    const updated = savedSearches.map((saved, i) => (i === index ? { ...saved, notifyEnabled: !saved.notifyEnabled } : saved));
    persist(
      updated,
      target.notifyEnabled ? `Alerts off for "${target.name}".` : `Alerts on for "${target.name}". We'll email you new matches.`
    );
  };

  const deleteSearch = (index) => {
    const target = savedSearches[index];
    setConfirmDeleteIndex(null);
    persist(savedSearches.filter((_, i) => i !== index), `Deleted "${target.name}".`);
  };

  const renderSaveArea = () => {
    if (!hasCriteria) {
      return (
        <p className={classes.hint}>
          <i className="far fa-bell" aria-hidden="true" />
          Search or pick filters, then save the search to get an email when new cars match.
        </p>
      );
    }
    // Already saved: its row in the list, marked Showing, says so.
    if (currentSaved) return null;
    if (isNaming) {
      return (
        <form className={classes.saveForm} onSubmit={saveCurrentSearch}>
          <label htmlFor="saved-search-name" className={classes.formLabel}>Name this search</label>
          <input
            id="saved-search-name"
            className={classes.nameInput}
            value={name}
            maxLength={NAME_MAX_LENGTH}
            autoFocus
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              setName(e.target.value);
              setError('');
            }}
          />
          {error && <p className={classes.error}>{error}</p>}
          <label className={classes.alertOption}>
            <input type="checkbox" role="switch" checked={alertsOn} onChange={() => setAlertsOn((on) => !on)} />
            <span>
              <strong>Email me new matches</strong>
              <small>We check new {adapter.listingsLabel} every few hours.</small>
            </span>
          </label>
          <div className={classes.formActions}>
            <button type="button" className={classes.secondaryButton} onClick={() => setIsNaming(false)}>Cancel</button>
            <button type="submit" className={classes.primaryButton}>Save search</button>
          </div>
        </form>
      );
    }
    return (
      <button type="button" className={classes.saveButton} onClick={startNaming}>
        <i className="far fa-bell" aria-hidden="true" />
        {isLoggedIn ? 'Save search & get alerts' : 'Log in to save this search'}
      </button>
    );
  };

  return (
    <div className={classes.savedSearches}>
      {renderSaveArea()}

      {isLoggedIn && savedSearches.length > 0 && (
        <section className={classes.list} aria-label="Saved searches">
          <div className={classes.listHeading}>
            <button type="button" className={classes.listToggle} onClick={toggleCollapsed} aria-expanded={!collapsed}>
              <i className={`fas fa-chevron-down ${classes.chevron} ${collapsed ? classes.chevronCollapsed : ''}`} aria-hidden="true" />
              <h5>Saved searches</h5>
              <span className={classes.count}>{savedSearches.length}</span>
            </button>
            <div className={classes.alertsLabel}>
              <span className={classes.columnLabel}>{collapsed ? `${alertsOnCount} with alerts on` : 'Email alerts'}</span>
              <span className={classes.infoTip}>
                <button
                  type="button"
                  className={classes.infoButton}
                  aria-label="What do email alerts do?"
                  aria-expanded={infoOpen}
                  aria-describedby="saved-search-alerts-info"
                  onClick={() => setInfoOpen((open) => !open)}
                  onBlur={() => setInfoOpen(false)}
                >
                  <i className="fas fa-info-circle" aria-hidden="true" />
                </button>
                <span id="saved-search-alerts-info" role="tooltip" className={`${classes.infoBubble} ${infoOpen ? classes.infoBubbleOpen : ''}`}>
                  <strong>Email alerts</strong>
                  Switch one on and we'll email you when new {adapter.carsLabel} match that saved search. You'll also see them under the bell at the top of the site. We check new listings every few hours.
                  <span className={classes.infoBubbleNote}>Switch it off to keep the search without getting emails.</span>
                </span>
              </span>
            </div>
          </div>
          {visibleSearches.length > 0 && (
          <ul>
            {visibleSearches.map(({ saved, index }) => {
              const isCurrent = index === currentIndex;
              const summary = adapter.describe(saved).join(' · ') || 'All cars';
              // A suggested name is the summary, so it would only repeat it.
              const showSummary = summary.toLowerCase() !== saved.name.trim().toLowerCase();
              return (
                <li key={`${saved.name}-${index}`} className={`${classes.row} ${isCurrent ? classes.rowCurrent : ''}`}>
                  {confirmDeleteIndex === index ? (
                    <div className={classes.confirm}>
                      <p>Delete <strong>{saved.name}</strong>?</p>
                      <div className={classes.confirmActions}>
                        <button type="button" className={classes.secondaryButton} onClick={() => setConfirmDeleteIndex(null)}>Cancel</button>
                        <button type="button" className={classes.dangerButton} onClick={() => deleteSearch(index)}>Delete</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        className={classes.rowMain}
                        onClick={() => onApply(saved)}
                        title={isCurrent ? 'These are the cars on screen now' : 'Show these cars'}
                      >
                        <span className={classes.rowName}>{saved.name}</span>
                        {(isCurrent || showSummary) && (
                          <span className={classes.rowSummary}>
                            {isCurrent && <span className={classes.currentBadge}>Showing</span>}
                            {showSummary && summary}
                          </span>
                        )}
                      </button>
                      <div className={classes.rowActions}>
                        <AlertSwitch
                          checked={Boolean(saved.notifyEnabled)}
                          onToggle={() => toggleAlerts(index)}
                          label={`Email alerts for ${saved.name}`}
                        />
                        <button
                          type="button"
                          className={classes.deleteButton}
                          aria-label={`Delete ${saved.name}`}
                          title="Delete this saved search"
                          onClick={() => setConfirmDeleteIndex(index)}
                        >
                          <i className="far fa-trash-alt" aria-hidden="true" />
                        </button>
                      </div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
          )}
          {!collapsed && savedSearches.length > VISIBLE_SEARCHES && (
            <button type="button" className={classes.showMore} onClick={() => setShowAll((all) => !all)}>
              {showAll ? 'Show fewer' : `Show all ${savedSearches.length}`}
            </button>
          )}
        </section>
      )}
    </div>
  );
};

export default SavedSearches;
